/* eslint-disable consistent-return */
import { isNumber } from 'lodash';
import ExcelJS from 'exceljs';

export const ValidationType = {
  NonBlank: 'NonBlank',
  NonDuplicate: 'NonDuplicate',
  Numeric: 'Numeric',
  Latitude: 'Latitude',
  Longitude: 'Longitude',
  Boolean: 'Boolean',
};

const RuleValidatorMap = {
  [ValidationType.NonBlank]: {
    validator: (c) =>
      c?.value !== null &&
      c?.value !== undefined &&
      (isNumber(c.value) ||
        (typeof c.value === 'string' && c.value.length > 0) ||
        typeof c.value === 'boolean'),
    errorMsgTmp: 'Cell <position> on sheet <sheet name> should not be blank.',
  },
  [ValidationType.NonDuplicate]: {
    validator: (c, col) =>
      !c || (c.value && col.filter((e) => c.value === e).length === 1),
    errorMsgTmp: '<value> cannot be duplicate on sheet <sheet name>.',
  },
  [ValidationType.Numeric]: {
    validator: (c) =>
      !c || typeof c.value === 'number' || !Number.isNaN(Number(c.value)),
    errorMsgTmp: 'Cell <position> on sheet <sheet name> should be a number.',
  },
  [ValidationType.Latitude]: {
    validator: (c) => c?.value >= -90 && c?.value <= 90,
    errorMsgTmp:
      'Cell <position> on sheet <sheet name> should be between from -90 and 90.',
  },
  [ValidationType.Longitude]: {
    validator: (c) => c?.value >= -180 && c?.value <= 180,
    errorMsgTmp:
      'Cell <position> on sheet <sheet name> should be between from -180 and 180.',
  },
  [ValidationType.Boolean]: {
    validator: (c) =>
      ['TRUE', 'FALSE', true, false].includes(c?.value) ||
      typeof c?.value === 'boolean',
    errorMsgTmp:
      'Cell <position> on sheet <sheet name> should be TRUE or FALSE.',
  },
};

export default class ExcelValidator {
  constructor(config) {
    this.sheets = Object.keys(config).map((sheetName) =>
      Object.assign({}, config[sheetName], { name: sheetName }),
    );
    if (this.sheets.length === 0) {
      throw new Error('invalid rules');
    }
    this.errors = [];
    this.sheetName = null;
    this.sheet = null;
    this.cols = null;
    this.rows = null;
    this.cellsOfSheet = null;
    this.rules = null;
    this.colsArray = null;
    this.rowsArray = null;
  }

  static splitXY(cell) {
    const colMatch = cell.match(/[A-Z]+/g);
    const rowMatch = cell.match(/[0-9]+/g);
    return [
      colMatch ? colMatch[0] : '',
      rowMatch ? parseInt(rowMatch[0], 10) : 0,
    ];
  }

  static recursivelyFlip = (c) => {
    if (!c || c === '') return 'A';
    const char = c.slice(-1);
    if (char === 'Z') {
      const prefix = c.slice(0, -1);
      return `${prefix ? ExcelValidator.recursivelyFlip(prefix) : 'A'}A`;
    }
    return c.slice(0, -1) + String.fromCharCode(char.charCodeAt(0) + 1);
  };

  static getColRangeArr(l, r) {
    const rangeArr = [l];
    let current = l;
    while (current !== r) {
      current = ExcelValidator.recursivelyFlip(current);
      rangeArr.push(current);
    }
    return rangeArr;
  }

  static numberToColumn(num) {
    let result = '';
    while (num > 0) {
      num--;
      result = String.fromCharCode(65 + (num % 26)) + result;
      num = Math.floor(num / 26);
    }
    return result;
  }

  static async readFromArrayBuffer(arrayBuffer) {
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(arrayBuffer);
    return workbook;
  }

  static parseSheet(sheetRules, worksheet) {
    const headerRange = sheetRules.header.split(':');
    const headerColRange = [
      ExcelValidator.splitXY(headerRange[0])[0],
      ExcelValidator.splitXY(headerRange[1])[0],
    ];
    const colRangeArray = ExcelValidator.getColRangeArr(...headerColRange);
    const rowOffSet = ExcelValidator.splitXY(headerRange[0])[1];

    const cols = colRangeArray.reduce(
      (acc, col) => ({ ...acc, [col]: [] }),
      {},
    );
    const rows = {};
    const sheetData = {};

    // Safely iterate through worksheet
    if (worksheet && worksheet.eachRow) {
      worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
        if (row && row.eachCell) {
          row.eachCell({ includeEmpty: false }, (cell, colNumber) => {
            const colLetter = ExcelValidator.numberToColumn(colNumber);
            const cellAddress = `${colLetter}${rowNumber}`;

            // Handle different cell value types from ExcelJS
            let cellValue = cell.value;

            // Handle rich text
            if (cellValue && typeof cellValue === 'object') {
              if (cellValue.richText) {
                cellValue = cellValue.richText.map((rt) => rt.text).join('');
              } else if (cellValue.text) {
                cellValue = cellValue.text;
              } else if (cellValue.result !== undefined) {
                cellValue = cellValue.result; // Formula result
              }
            }

            sheetData[cellAddress] = {
              value: cellValue,
              address: cellAddress,
              type: typeof cellValue,
            };
          });
        }
      });
    }

    // Build cols and rows structures
    Object.keys(sheetData).forEach((k) => {
      const [x, y] = ExcelValidator.splitXY(k);
      if (!colRangeArray.includes(x)) {
        return;
      }
      if (y <= rowOffSet) {
        return;
      }
      if (cols[x]) {
        cols[x] = [...cols[x], k];
      }
      if (!rows[y]) {
        rows[y] = [k];
      } else {
        rows[y] = [...rows[y], k];
      }
    });

    return { cols, rows, sheetData };
  }

  genErrorMsg(rule, cell, arg) {
    const msgTmp = RuleValidatorMap[rule]?.errorMsgTmp;
    if (!msgTmp) return `Validation failed for cell ${cell}`;

    const value = this.sheet?.[cell]?.value || '';

    let msg = typeof msgTmp === 'function' ? msgTmp(arg) : msgTmp;

    const replacement = {
      '<position>': cell,
      '<sheet name>': this.sheetName,
      '<value>': value,
    };
    Object.keys(replacement).forEach((k) => {
      msg = msg.replace(k, replacement[k]);
    });
    return msg;
  }

  customizedValidate(validator, position, cellsOfCol) {
    const cell = this.sheet?.[position];
    const result = validator(cell, position, cellsOfCol, this.cellsOfSheet);
    if (result === true) {
      return;
    }
    this.errors.push(result);
  }

  validateCell(r, col, cellsOfCol) {
    return (rowNum) => {
      const cell = `${col}${rowNum}`;
      let ruleViolated;
      let rules;
      let res = true;

      if (typeof r.rule === 'string') {
        rules = [r.rule];
      } else if (Array.isArray(r.rule)) {
        rules = r.rule;
      } else if (typeof r.validator === 'function') {
        return this.customizedValidate(r.validator, cell, cellsOfCol);
      } else {
        throw new Error(
          'types other than string, function or array are prohibited',
        );
      }

      for (const rule of rules) {
        const ruleValidator = RuleValidatorMap[rule]?.validator;
        if (!ruleValidator) continue;

        const result = ruleValidator(this.sheet?.[cell], cellsOfCol);
        if (result !== true) {
          ruleViolated = rule;
          res = result;
          break;
        }
      }

      if (ruleViolated) {
        const errorMsg = this.genErrorMsg(ruleViolated, cell, res);
        this.errors.push(errorMsg);
      }
    };
  }

  validateCol(ruleOfCol, vals) {
    const { validator } = ruleOfCol;
    if (typeof validator !== 'function') {
      throw new Error('rule of a column should be a function');
    }
    const result = ruleOfCol.validator(vals, this.sheetName);
    if (result === true) {
      return;
    }
    this.errors.push(result);
  }

  validateSheet() {
    if (!this.colsArray || !this.rowsArray) return;

    for (let i = 0; i < this.colsArray.length; i++) {
      const col = this.colsArray[i];
      const r = this.rules?.find(
        (r) => r.col === col && (r.type === 'cell' || !r.type),
      );
      if (!r) {
        continue;
      }
      const cellsOfCol = this.cellsOfSheet?.[i] || [];
      const ruleOfCol = this.rules?.find(
        (e) => e.col === col && e.type === 'col',
      );

      if (ruleOfCol) {
        this.validateCol(ruleOfCol, cellsOfCol);
      }

      this.rowsArray.forEach(this.validateCell(r, col, cellsOfCol));
    }
  }

  processSheet(wb, sRule) {
    this.sheetName = sRule.name;
    this.rules = sRule.rules;

    // Try to get worksheet by name
    let worksheet = wb.getWorksheet(sRule.name);

    // If not found by name, try by index (1-based in ExcelJS)
    if (!worksheet) {
      const worksheets = wb.worksheets;
      if (worksheets && worksheets.length > 0) {
        // Try to find by case-insensitive name match
        worksheet = worksheets.find(
          (ws) => ws.name?.toLowerCase() === sRule.name?.toLowerCase(),
        );

        // If still not found, use first worksheet as fallback
        if (!worksheet) {
          console.warn(
            `Worksheet "${sRule.name}" not found, available sheets:`,
            worksheets.map((ws) => ws.name),
          );
          throw new Error(`Worksheet "${sRule.name}" not found`);
        }
      }
    }

    const { cols, rows, sheetData } = ExcelValidator.parseSheet(
      sRule,
      worksheet,
    );
    this.sheet = sheetData;
    this.cols = cols;
    this.rows = rows;
    this.rowsArray = Object.keys(rows);
    this.colsArray = Object.keys(cols);
    this.cellsOfSheet = this.colsArray.map((col) =>
      (cols[col] || []).map((cell) => this.sheet?.[cell]?.value),
    );
  }

  async validate(input, validator) {
    try {
      let wb;

      if (input instanceof ArrayBuffer) {
        wb = await ExcelValidator.readFromArrayBuffer(input);
      } else if (typeof input === 'string') {
        // Convert binary string to ArrayBuffer
        const buffer = new ArrayBuffer(input.length);
        const view = new Uint8Array(buffer);
        for (let i = 0; i < input.length; i++) {
          view[i] = input.charCodeAt(i) & 0xff;
        }
        wb = await ExcelValidator.readFromArrayBuffer(buffer);
      } else {
        wb = input;
      }

      // Run custom validator if provided
      if (typeof validator === 'function') {
        const checkRes = validator(wb);
        if (checkRes !== true) {
          this.errors.push(checkRes);
        }
      }

      // Process each sheet
      for (const sRule of this.sheets) {
        try {
          this.processSheet(wb, sRule);
          this.validateSheet();
        } catch (sheetError) {
          this.errors.push(sheetError.message);
        }
      }

      return { errors: [...new Set(this.errors)] };
    } catch (error) {
      console.error('ExcelValidator error:', error);
      return { errors: [error.message || 'Failed to validate Excel file'] };
    }
  }
}
